import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import ZiggyContext from "../../../store/ziggy-context";
import axios from "axios";
import {Backdrop, CircularProgress, Stack, Typography} from "@mui/material";
import Page from "../../ui/Page";
import "react-datetime/css/react-datetime.css";
import "../styles.css";
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, TimeScale, Title, Tooltip,} from 'chart.js';
import {Chart} from 'react-chartjs-2';
import Box from "@mui/material/Box";
import 'chart.js/auto';
import "chartjs-adapter-luxon";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import SampleOrdersReports from "./SampleOrdersReports";
import 'chartjs-plugin-annotation';
import {largeNumberFormatted} from "../../../utils/largeNumberFormatted";
import {PageContext, EmptyState} from "@lb/mlib-components";
import {DateTime} from "luxon";

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function Samples() {
    const pageCtx = useContext(PageContext)
    useEffect(() => pageCtx.updateTitle('Reporting | Sample Orders'), [pageCtx])

    const ziggyCtx = useContext(ZiggyContext);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState();
    const ref = useRef();

    const [dealer, setDealer] = useState('all');

    function handleChangeDealer(event) {
        setDealer(event.target.value)
    }

    const showOptions = [
        {
            title: 'Number of Sample Orders (Last 90 Days)',
            value: 'orders_daily',
            description: 'Total quantity of sample orders for the last 90 days.'
        },
        {
            title: 'Number of Sample Orders Weekly',
            value: 'orders_weekly',
            description: 'Total quantity of sample orders for each week over the span of 52 weeks.'
        },
        {
            title: 'Quantity of Line Items (Last 90 Days)',
            value: 'items_daily',
            description: 'Total quantity of line items in sample orders for the last 90 days.'
        },
        {
            title: 'Quantity of Line Items Weekly',
            value: 'items_weekly',
            description: 'Total quantity of line items in sample orders for each week over the span of 52 weeks.'
        }
    ];


    const [show, setShow] = useState(showOptions.find((option) => option.value === 'orders_daily'));

    const handleChangeShow = (event) => {
        const value = event.target.value;
        const option = showOptions.find((option) => option.value === value);
        setShow(option);
    };

    const {apiUrl} = window['runConfig'];


    const loadChartDataFromServer = useCallback(() => {
        setLoading(true);

        const isdealerParam = dealer === 'all' ? '' : `isdealer=${dealer === 'isdealer'}`;
        axios.get(apiUrl + `/samples/analysis?${isdealerParam}`).then((response) => {
            const baseData = response.data[show?.value]?.map(e => {
                return {
                    x: e.calendar_date, y: e.amount
                }
            });

            const average = response.data[show?.value]?.map(e => {
                return {
                    x: e.calendar_date, y: e.cumulative_avg
                }
            });

            let data = [];

            data.push({
                type: 'bar',
                label: show.title,
                data: baseData,
                borderColor: 'rgb(255,99,99)',
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                tension: 0.5,
                spanGaps: false

            })

            if (average)
                data.push({
                    type: 'line',
                    label: 'Cumulative Average',
                    data: average,
                    borderColor: 'rgb(99,165,218)',
                    borderDash: [5, 5],
                })


            setData({
                datasets: data,
            })

        }).finally(() => {
            setLoading(false);
        })

    }, [dealer, apiUrl, show, ziggyCtx.warehouse, ziggyCtx.load]);

    useEffect(() => {
        loadChartDataFromServer()
    }, [loadChartDataFromServer])


    const options = {
        interaction: {intersect: false},
        responsive: true,

        plugins: {
            legend: {
                position: 'top',
                labels: {
                    boxHeight: (context) => {
                        const i = context.datasetIndex;
                        if (i === 0) return 20;
                        if (i === 1) return 1;
                        return 1;
                    },
                },
            },

            tooltip: {
                callbacks: {
                    beforeLabel: (context) => {
                        let dt = DateTime.fromMillis(context.parsed.x);
                        if (context.dataset?.label === 'Previous Year') {
                            dt = dt.minus({years: 1});
                        }
                        return dt.toFormat('MMM dd, yyyy');
                    },
                    title: () => '',
                    label: (context) => largeNumberFormatted(context.parsed.y),
                },
            },
        },

        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'day',
                    parser: 'yyyy-L-d',
                    tooltipFormat: 'MMMM d, yyyy',
                    stepSize: 1,
                },
                ticks: {source: 'data'},
                bounds: 'data',
            },
        },
    };


    return (
        <Page>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Backdrop
                    sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
                    open={loading}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>
                <Stack
                    direction={"row"}
                    spacing={2}
                    useFlexGap
                    sx={{
                        marginTop: 1,
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: 'wrap'
                    }}>
                    <Stack
                        direction={"row"}
                        spacing={2}
                        useFlexGap
                        sx={{
                            justifyContent: "flex-start",
                            alignItems: "center",
                            flexWrap: 'wrap'
                        }}>
                        <FormControl sx={{width: '200px'}}>
                            <InputLabel id="demo-simple-select-label">Filter by store</InputLabel>
                            <Select
                                id="outlined-select-warehouse"
                                label="Filter by store"
                                value={dealer}
                                onChange={handleChangeDealer}

                                size={"small"}

                            >
                                <MenuItem value={'all'}>
                                    All
                                </MenuItem>
                                <MenuItem value={'isdealer'}>
                                    Dealers
                                </MenuItem>
                                <MenuItem value={'public'}>
                                    Public
                                </MenuItem>
                            </Select>
                        </FormControl>


                        <FormControl sx={{maxWidth: 400, minWidth: 200, flex: '1 1 200px'}}>
                            <InputLabel id="demo-simple-select-label">Select Measure</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={show?.value}
                                label={'Select Measure'}
                                onChange={handleChangeShow}
                                size={"small"}
                            >
                                {showOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {show?.description && <Box>
                            <Typography>{show?.description}</Typography>
                        </Box>}

                        <SampleOrdersReports/>
                    </Stack>

                </Stack>
            </LocalizationProvider>
            <Box sx={{marginTop: 3}}>
                {data ? <Chart type={'bar'} options={options} ref={ref} data={data}/> :
                    !loading && <Box sx={{
                        marginLeft: 3
                    }}><EmptyState message="No data to show."/></Box>}
            </Box>


        </Page>
    );
}
;